/**
 * useSimpleConversations — the rail's conversation list, most recent first,
 * scoped to the active module and to the conversations the person takes part in (`involvesMe`, server
 * `conversation-involvement.ts`). Polls so a conversation that starts needing a
 * human shows its attention dot without a reload.
 *
 * `search` narrows to the topics that contain it; `groupId` to one group, or `none` for the conversations in no group
 * (the rail's sections). It reads a page at a time (`showOlder` reads the next), so the rail's older day and week
 * sections are reachable, not cut off after the first page. It is the generated `listConversations` query, so anything
 * that invalidates `listConversationsQueryKey()` (the row menu, starting a conversation) refreshes every section.
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import { listConversationsInfiniteOptions } from '../generated/api/@tanstack/react-query.gen.js';

interface SimpleConversationsOptions {
  search?: string;
  groupId?: string;
  /** Read only when shown (a closed group reads nothing). */
  enabled?: boolean;
}

const PAGE_SIZE = 50;

export function useSimpleConversations(moduleId: string, options: SimpleConversationsOptions = {}) {
  const search = options.search?.trim() ?? '';
  const query = useInfiniteQuery({
    ...listConversationsInfiniteOptions({
      query: {
        ...(moduleId && moduleId !== 'all' ? { moduleId } : {}),
        ...(search ? { q: search } : {}),
        ...(options.groupId ? { groupId: options.groupId } : {}),
        // Only the conversations the person takes part in (owner 2026-11-01): never the machine runs and members'
        // threads a role that sees the whole organization would otherwise list.
        involvesMe: true,
        limit: PAGE_SIZE,
        sort_by: 'lastActivityAt',
        sort_order: 'desc',
      },
    }),
    initialPageParam: { query: {} },
    getNextPageParam: (last) =>
      last.paging.hasMore && last.paging.cursors.after ? { query: { after: last.paging.cursors.after } } : undefined,
    refetchInterval: 15_000,
    enabled: options.enabled ?? true,
  });
  if (query.isError) console.error('useSimpleConversations: failed to load conversations', query.error);
  return {
    data: query.data?.pages.flatMap((page) => page.data),
    isPending: query.isPending,
    isSuccess: query.isSuccess,
    isError: query.isError,
    refetch: query.refetch,
    hasOlder: query.hasNextPage,
    loadingOlder: query.isFetchingNextPage,
    showOlder: () => void query.fetchNextPage(),
  };
}
